// Tool implementations. Each one takes an HttpClient + the validated input and
// returns an MCP CallToolResult routed through formatToolResponse for the
// three-part shape (NL + structured + footer) per ADR-039 / contract #12.
// Keeping these as pure functions of (client, input) means tests don't need a
// running server — just a stub client that returns canned JSON.

import type {
  ApplicablePoliciesResponse,
  AskResult,
  BlastRadiusAffectedNode,
  BlastRadiusResult,
  Divergence,
  DivergenceResult,
  DivergenceType,
  ErrorEvent,
  ExpandResult,
  GraphEdge,
  GraphNode,
  HypotheticalAction,
  IncidentCard,
  ObservedDependenciesResult,
  PolicyViolation,
  RelateResult,
  RootCauseResult,
  TransitiveDependenciesResult,
} from '@neat.is/types'
import { Provenance } from '@neat.is/types'
import { HttpError, ProjectNotFoundError, type HttpClient } from './client.js'
import {
  formatEmptyResponse,
  formatErrorResponse,
  formatToolResponse,
  type ToolResponse,
} from './format.js'

export type { ToolResponse } from './format.js'

// Project-aware path builder. When `project` is set, route through
// /projects/<name>/...; otherwise hit the legacy root URL (which the core
// resolves to project=`default`). Keeping the legacy path means callers
// running an older core still talk to a known route.
function projectPath(project: string | undefined, suffix: string): string {
  if (!project) return suffix
  return `/projects/${encodeURIComponent(project)}${suffix}`
}

// The slice of the per-project /health response the readiness probe reads.
// #883 puts the most recent extraction pass's coverage here; `skippedFiles`
// is the exact count of files that failed to parse that pass (0 = a clean,
// complete pass). The field is absent until a full pass has recorded one.
interface HealthReadiness {
  coverage?: { skippedFiles?: number }
}

// An empty result is ambiguous: "no such edges exist" reads identically to
// "the first extraction scan hasn't finished, so I haven't seen them yet."
// A dead daemon is already unambiguous — the transport raises an isError, not
// an empty 200 — but a reachable daemon mid-first-scan returns 200 with a
// partial graph. `/health` already carries the readiness signal (#883), so on
// the empty path we read it and append a one-clause reason. Best-effort: if
// the probe itself can't answer, we return undefined and leave the empty
// message plain rather than turning it into an error.
async function readinessReason(
  client: HttpClient,
  project: string | undefined,
): Promise<string | undefined> {
  let health: HealthReadiness
  try {
    // The extraction coverage (#883) rides on the PER-PROJECT /health, which is
    // mounted only under /projects/:project. The unprefixed /health is the
    // daemon-wide probe and carries no coverage, so we name the project even
    // when the caller didn't: an unset project resolves to the documented
    // 'default' (ADR-026), which a per-project daemon maps to its one project.
    const named = project ?? 'default'
    health = await client.get<HealthReadiness>(projectPath(named, '/health'))
  } catch {
    return undefined
  }
  const coverage = health.coverage
  if (!coverage || typeof coverage.skippedFiles !== 'number') {
    // No full pass has recorded coverage yet — the first scan is still in
    // flight, so an empty answer can't be trusted as complete.
    return '(index still building — the first extraction pass has not finished yet, so this may be incomplete; retry once it settles.)'
  }
  if (coverage.skippedFiles > 0) {
    const n = coverage.skippedFiles
    return `(index partial — ${n} file${n === 1 ? '' : 's'} failed to parse in the last pass, so this may be incomplete.)`
  }
  // A clean, complete pass is on record — the empty result is a real answer,
  // not a still-building index.
  return '(graph is current — this is a real empty result, not a still-building index.)'
}

// Wrap an empty summary with the readiness clause so the agent can tell a
// genuinely-empty answer from a not-yet-indexed one. Falls back to the plain
// empty response when readiness can't be determined. The footer stays
// n/a · n/a and the three-part shape is unchanged.
async function emptyWithReadiness(
  client: HttpClient,
  project: string | undefined,
  summary: string,
): Promise<ToolResponse> {
  const reason = await readinessReason(client, project)
  return reason ? formatToolResponse({ summary: `${summary} ${reason}` }) : formatEmptyResponse(summary)
}

// Most tools want "node missing → friendly message, anything else → real error".
// The node-not-found 404 is itself an empty result, so it carries the same
// readiness clause: "node X not found" during a first scan means "not indexed
// yet," not "gone." client + project let the fallback probe /health.
async function withMissingNodeFallback(
  client: HttpClient,
  project: string | undefined,
  fn: () => Promise<ToolResponse>,
  notFoundMessage: string,
): Promise<ToolResponse> {
  try {
    return await fn()
  } catch (err) {
    // A project-not-found 404 is NOT a missing node — the core doesn't serve
    // this project at all, so an empty "no results" would be a confident answer
    // about the wrong codebase (#884). Surface it as a real error. Checked
    // before the generic 404 since ProjectNotFoundError extends HttpError.
    if (err instanceof ProjectNotFoundError) {
      return formatErrorResponse(err.message)
    }
    if (err instanceof HttpError && err.status === 404) {
      return emptyWithReadiness(client, project, notFoundMessage)
    }
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface RootCauseInput {
  errorNode: string
  errorId?: string
  project?: string
}

export async function getRootCause(client: HttpClient, input: RootCauseInput): Promise<ToolResponse> {
  const qs = input.errorId ? `?errorId=${encodeURIComponent(input.errorId)}` : ''
  const path = projectPath(
    input.project,
    `/graph/root-cause/${encodeURIComponent(input.errorNode)}${qs}`,
  )

  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<RootCauseResult>(path)
    const arrowPath = result.traversalPath.join(' ← ')
    const provenances = result.edgeProvenances.length
      ? result.edgeProvenances.join(', ')
      : '(direct, no edges traversed)'
    const summary =
      `Root cause for ${input.errorNode} is ${result.rootCauseNode}. ` +
      result.rootCauseReason +
      (result.fixRecommendation ? ` Recommended fix: ${result.fixRecommendation}.` : '')
    const blockLines = [
      `Traversal path: ${arrowPath}`,
      `Edge provenances: ${provenances}`,
    ]
    if (result.fixRecommendation) {
      blockLines.push(`Recommended fix: ${result.fixRecommendation}`)
    }
    // Navigation (ADR-189): show the ranked candidate set with per-node
    // classification so the agent can weigh alternatives, not just relay one
    // verdict. A symptom-only node is a downstream victim — not the cause.
    if (result.candidates && result.candidates.length > 0) {
      blockLines.push('', 'Candidates (ranked, most likely cause first):')
      for (const c of result.candidates) {
        blockLines.push(
          `  • ${c.node} — ${c.classification} (confidence ${c.confidence.toFixed(2)}): ${c.reason}`,
        )
      }
    }
    return formatToolResponse({
      summary,
      block: blockLines.join('\n'),
      confidence: result.confidence,
      provenance: result.edgeProvenances.length ? result.edgeProvenances : undefined,
    })
  }, `No root cause found for ${input.errorNode}. The node may be healthy, or it may not exist in the graph.`)
}

export interface BlastRadiusInput {
  nodeId: string
  depth?: number
  project?: string
}

export async function getBlastRadius(
  client: HttpClient,
  input: BlastRadiusInput,
): Promise<ToolResponse> {
  const qs = input.depth !== undefined ? `?depth=${input.depth}` : ''
  const path = projectPath(
    input.project,
    `/graph/blast-radius/${encodeURIComponent(input.nodeId)}${qs}`,
  )

  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<BlastRadiusResult>(path)
    if (result.totalAffected === 0) {
      return emptyWithReadiness(
        client,
        input.project,
        `${result.origin} has no dependents. Nothing else would break if it failed.`,
      )
    }
    const sorted = [...result.affectedNodes].sort(
      (a, b) => a.distance - b.distance || a.nodeId.localeCompare(b.nodeId),
    )
    const blockLines = sorted.map(formatBlastEntry)
    // Worst-case confidence — the path with the lowest cascaded confidence
    // is the headline number; agents should treat this as "what's the
    // weakest reachability NEAT actually knows about?"
    const minConfidence = sorted.reduce(
      (m, n) => Math.min(m, n.confidence),
      Number.POSITIVE_INFINITY,
    )
    const provenances = [...new Set(sorted.map((n) => n.edgeProvenance))]
    return formatToolResponse({
      summary: `Blast radius for ${result.origin}: ${result.totalAffected} dependent node${result.totalAffected === 1 ? '' : 's'} would break if it changed.`,
      block: blockLines.join('\n'),
      confidence: Number.isFinite(minConfidence) ? minConfidence : undefined,
      provenance: provenances.length ? provenances : undefined,
    })
  }, `Node ${input.nodeId} not found in the graph.`)
}

function formatBlastEntry(n: BlastRadiusAffectedNode): string {
  const tag = n.edgeProvenance === Provenance.STALE ? ' [STALE — last seen too long ago]' : ''
  return `  • ${n.nodeId} (distance ${n.distance}, ${n.edgeProvenance})${tag}`
}

export interface DependenciesInput {
  nodeId: string
  // BFS depth. Default 3; max 10. Direct-only consumers pass 1.
  depth?: number
  project?: string
}

// Transitive get_dependencies (issue #144). Calls the core endpoint
// /graph/dependencies/:nodeId?depth=N which BFS-walks outbound. The output
// groups results by hop so direct dependencies stand out from transitives —
// agents asked "what does X depend on?" usually want the direct list with
// transitives as context.
export async function getDependencies(
  client: HttpClient,
  input: DependenciesInput,
): Promise<ToolResponse> {
  const depth = input.depth ?? 3
  const path = projectPath(
    input.project,
    `/graph/dependencies/${encodeURIComponent(input.nodeId)}?depth=${depth}`,
  )

  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<TransitiveDependenciesResult>(path)
    if (result.total === 0) {
      return emptyWithReadiness(
        client,
        input.project,
        depth === 1
          ? `${input.nodeId} has no direct dependencies in the graph.`
          : `${input.nodeId} has no dependencies (BFS to depth ${depth}).`,
      )
    }
    // Group by distance so the structured block reads as concentric rings.
    const byDistance = new Map<number, typeof result.dependencies>()
    for (const dep of result.dependencies) {
      const ring = byDistance.get(dep.distance) ?? []
      ring.push(dep)
      byDistance.set(dep.distance, ring)
    }
    const blockLines: string[] = []
    for (const distance of [...byDistance.keys()].sort((a, b) => a - b)) {
      const label = distance === 1 ? 'Direct (distance 1)' : `Distance ${distance}`
      blockLines.push(`${label}:`)
      for (const dep of byDistance.get(distance)!) {
        blockLines.push(`  • ${dep.nodeId} — ${dep.edgeType} (${dep.provenance})`)
      }
    }
    const provenances = [...new Set(result.dependencies.map((d) => d.provenance))]
    const directCount = byDistance.get(1)?.length ?? 0
    const summary =
      depth === 1
        ? `${input.nodeId} has ${directCount} direct dependenc${directCount === 1 ? 'y' : 'ies'}.`
        : `${input.nodeId} has ${result.total} dependenc${result.total === 1 ? 'y' : 'ies'} reachable to depth ${depth} (${directCount} direct).`
    return formatToolResponse({
      summary,
      block: blockLines.join('\n'),
      provenance: provenances,
    })
  }, `Node ${input.nodeId} not found in the graph.`)
}

// Render one OBSERVED dependency, file-grained. When the edge source isn't the
// queried node — a service's owned file made the call — name that file, so the
// answer stays file-first rather than a service rollup (file-awareness §3).
function observedDepLine(nodeId: string, e: GraphEdge): string {
  const via = e.source !== nodeId ? ` (via ${e.source})` : ''
  return `  • ${e.target} — ${e.type}${via}${edgeMeta(e)}`
}

export async function getObservedDependencies(
  client: HttpClient,
  input: DependenciesInput,
): Promise<ToolResponse> {
  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<ObservedDependenciesResult>(
      projectPath(
        input.project,
        `/graph/observed-dependencies/${encodeURIComponent(input.nodeId)}`,
      ),
    )
    if (result.dependencies.length === 0) {
      // A pure receiver is fully observed — it just calls nothing downstream.
      // Reporting "is OTel running?" at it would be wrong; that note is honest
      // only when nothing has been observed at all and static deps exist.
      if (result.observed) {
        return formatToolResponse({
          summary:
            `${input.nodeId} makes no outbound runtime calls, but OTel has observed it ` +
            `receiving traffic on ${result.inboundObservedCount} inbound call ` +
            `path${result.inboundObservedCount === 1 ? '' : 's'} — it's a pure receiver.`,
          provenance: Provenance.OBSERVED,
        })
      }
      const note = result.hasExtractedOutbound
        ? ' Static (EXTRACTED) dependencies exist but no runtime traffic has been seen — is OTel running?'
        : ''
      return formatEmptyResponse(`No OBSERVED dependencies for ${input.nodeId}.${note}`)
    }
    const blockLines = result.dependencies.map((e) => observedDepLine(input.nodeId, e))
    return formatToolResponse({
      summary: `${input.nodeId} has ${result.dependencies.length} runtime dependenc${result.dependencies.length === 1 ? 'y' : 'ies'} confirmed by OTel.`,
      block: blockLines.join('\n'),
      provenance: Provenance.OBSERVED,
    })
  }, `Node ${input.nodeId} not found in the graph.`)
}

function edgeMeta(e: GraphEdge): string {
  const bits: string[] = []
  if (e.signal) {
    // Prefer the runtime signal numbers — "saw 1,247 calls, 3 errors" reads
    // better than a derived 0.94 confidence.
    bits.push(`spans=${e.signal.spanCount}`)
    if (e.signal.errorCount > 0) bits.push(`errors=${e.signal.errorCount}`)
    if (e.signal.lastObservedAgeMs !== undefined) {
      bits.push(`age=${formatDuration(e.signal.lastObservedAgeMs)}`)
    }
  } else if (e.callCount !== undefined) {
    bits.push(`callCount=${e.callCount}`)
  }
  if (e.lastObserved) bits.push(`lastObserved=${e.lastObserved}`)
  if (e.confidence !== undefined) bits.push(`confidence=${e.confidence}`)
  return bits.length ? ` [${bits.join(', ')}]` : ''
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.round(h / 24)}d`
}

// Expand — one bidirectional navigation step (ADR-189). The agent walks the
// failure neighbourhood one legible hop at a time instead of relaying a verdict.
export interface ExpandInput {
  nodeId: string
  direction: 'up' | 'down'
  project?: string
}

export async function expandNode(client: HttpClient, input: ExpandInput): Promise<ToolResponse> {
  const path = projectPath(
    input.project,
    `/graph/expand/${encodeURIComponent(input.nodeId)}?direction=${input.direction}`,
  )
  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<ExpandResult>(path)
    const dirWord =
      input.direction === 'up' ? 'callers/dependents (up)' : 'callees/dependencies (down)'
    const summary =
      `${result.node.id} is ${result.node.classification}. ` +
      `${result.neighbours.length} ${dirWord}.`
    const blockLines = result.neighbours.map(
      (n) => `  • ${n.node} — ${n.classification} via ${n.edgeType} (${n.provenance})`,
    )
    return formatToolResponse({
      summary,
      block: blockLines.length ? blockLines.join('\n') : '(no runtime neighbours in this direction)',
    })
  }, `Node ${input.nodeId} not found in the graph.`)
}

// Relate — pairwise directed link-confirmation (ADR-189). Confirms a hypothesised
// cause→symptom link and whether the connecting path carries the failure.
export interface RelateInput {
  a: string
  b: string
  maxDepth?: number
  project?: string
}

export async function relate(client: HttpClient, input: RelateInput): Promise<ToolResponse> {
  const qs = input.maxDepth !== undefined ? `&maxDepth=${input.maxDepth}` : ''
  const path = projectPath(
    input.project,
    `/graph/relate?a=${encodeURIComponent(input.a)}&b=${encodeURIComponent(input.b)}${qs}`,
  )
  try {
    const result = await client.get<RelateResult>(path)
    if (!result.related) {
      return formatEmptyResponse(
        `${input.a} and ${input.b} are not related — ${result.note ?? 'no path found'}.`,
      )
    }
    const arrow =
      result.direction === 'a->b' ? `${input.a} → ${input.b}` : `${input.b} → ${input.a}`
    const carries = result.paths[0]?.carriesSignal
      ? 'and the path carries the failure end to end'
      : 'but the path carries no failure signal'
    const gap = result.grainGap ? ' (grain gap — only a coarser link is in evidence)' : ''
    const summary = `${arrow}: a path exists ${carries}${gap}.`
    const blockLines = result.paths.map(
      (p) => `  ${p.nodes.join(' → ')}  [${p.edgeTypes.join(', ')}]  carriesSignal=${p.carriesSignal}`,
    )
    return formatToolResponse({ summary, block: blockLines.join('\n') })
  } catch (err) {
    if (err instanceof ProjectNotFoundError) return formatErrorResponse(err.message)
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

// ask — the plain-language door over the whole tool surface (ADR-198). One
// natural-language question in; one compact, provenance-tagged answer out. The
// core resolves the question to nodes and routes it to the right traversal, so
// the agent never has to know WHICH structured tool or the exact node id first.
export interface AskInput {
  question: string
  project?: string
}

export async function ask(client: HttpClient, input: AskInput): Promise<ToolResponse> {
  const question = input.question.trim()
  if (!question) return formatEmptyResponse('ask: the question was empty.')
  return withMissingNodeFallback(client, input.project, async () => {
    const result = await client.get<AskResult>(
      projectPath(input.project, `/graph/ask?q=${encodeURIComponent(question)}`),
    )
    const blockLines: string[] = []
    if (result.matched.length > 0) {
      blockLines.push(
        `Matched (${result.intent}): ` +
          result.matched.map((m) => `${m.nodeId} [${m.via} ${m.score.toFixed(2)}]`).join(', '),
      )
    } else if (result.scope === 'global') {
      // A graph-wide answer to an entity-less question — no node was named.
      blockLines.push(`Graph-wide answer (${result.intent}) — no entity named.`)
    }
    for (const section of result.sections) {
      blockLines.push('', `${section.heading}:`)
      for (const fact of section.facts) {
        const tag = fact.provenance
          ? ` [${fact.provenance}${fact.confidence !== undefined ? ` ${fact.confidence.toFixed(2)}` : ''}]`
          : fact.confidence !== undefined
            ? ` [confidence ${fact.confidence.toFixed(2)}]`
            : ''
        blockLines.push(`  • ${fact.text}${tag}`)
      }
    }
    return formatToolResponse({
      summary: result.answer,
      block: blockLines.join('\n').trim(),
      ...(result.confidence !== undefined ? { confidence: result.confidence } : {}),
      ...(result.provenance.length > 0 ? { provenance: result.provenance } : {}),
    })
  }, `ask: nothing in "${question}" resolved to a node in the graph.`)
}

export interface IncidentHistoryInput {
  nodeId: string
  limit?: number
  project?: string
}

export async function getIncidentHistory(
  client: HttpClient,
  input: IncidentHistoryInput,
): Promise<ToolResponse> {
  return withMissingNodeFallback(client, input.project, async () => {
    const body = await client.get<{ count: number; total: number; events: ErrorEvent[] }>(
      projectPath(input.project, `/incidents/${encodeURIComponent(input.nodeId)}`),
    )
    const events = body.events
    if (events.length === 0) {
      return formatEmptyResponse(`No incidents recorded against ${input.nodeId}.`)
    }
    // ndjson order is append-time = oldest first. Reverse so the most recent
    // event leads, then trim to the requested limit.
    const ordered = [...events].reverse().slice(0, input.limit ?? 20)
    const blockLines: string[] = []
    for (const ev of ordered) {
      blockLines.push(`  ${ev.timestamp} — ${ev.service}: ${ev.errorMessage}`)
      blockLines.push(`    trace=${ev.traceId} span=${ev.spanId}`)
    }
    return formatToolResponse({
      summary: `${input.nodeId} has ${body.total} recorded incident${body.total === 1 ? '' : 's'}; showing the ${ordered.length} most recent.`,
      block: blockLines.join('\n'),
      // ErrorEvents are observation records, not graph edges — provenance is
      // OBSERVED by definition (the OTel span happened).
      provenance: Provenance.OBSERVED,
    })
  }, `Node ${input.nodeId} not found in the graph.`)
}

// get_incident_card — one self-sufficient work order for an incident (ADR-221).
// The incident fused with its root-cause chain, blast radius, governing policies,
// and node divergence, each claim provenance-stamped, so an agent can act without
// grepping. errorId pins one incident; default is the node's most recent.
export interface IncidentCardInput {
  nodeId: string
  errorId?: string
  project?: string
}

export async function getIncidentCard(
  client: HttpClient,
  input: IncidentCardInput,
): Promise<ToolResponse> {
  return withMissingNodeFallback(
    client,
    input.project,
    async () => {
      const qs = input.errorId ? `?errorId=${encodeURIComponent(input.errorId)}` : ''
      const card = await client.get<IncidentCard>(
        projectPath(input.project, `/graph/incident-card/${encodeURIComponent(input.nodeId)}${qs}`),
      )
      const lines: string[] = []
      lines.push(`  incident ${card.id} [${card.incidentKind}] at ${card.at}`)
      lines.push(`  ${card.message}`)
      if (card.locus) {
        const l = card.locus
        lines.push(
          `  locus: ${l.symbol ? `${l.symbol} ` : ''}${l.file}${l.lineStart ? `:${l.lineStart}` : ''} [${l.provenance}]`,
        )
      } else {
        lines.push('  locus: none recovered — service grain')
      }
      if (card.rootCause) {
        const rc = card.rootCause
        lines.push(
          `  root cause: ${rc.node}${rc.classification ? ` (${rc.classification})` : ''} — ${rc.reason} [confidence ${rc.confidence.toFixed(2)}]`,
        )
        lines.push(`  chain: ${rc.chain.map((h) => `${h.node} [${h.provenance}]`).join(' → ')}`)
        if (rc.fix) lines.push(`  fix: ${rc.fix}`)
      } else {
        lines.push('  root cause: none reachable')
      }
      if (card.blastRadius) {
        lines.push(
          `  blast radius: ${card.blastRadius.totalAffected} node(s); nearest ${card.blastRadius.nearest.map((n) => n.node).join(', ')}`,
        )
      }
      if (card.policies && card.policies.length > 0) {
        lines.push(
          `  policies: ${card.policies.map((p) => `[${p.severity}] ${p.policyName}`).join('; ')}`,
        )
      }
      if (card.divergence && card.divergence.length > 0) {
        lines.push(
          `  divergence: ${card.divergence.map((d) => `${d.type} (${d.summary})`).join('; ')}`,
        )
      }
      return formatToolResponse({
        summary: card.headline,
        block: lines.join('\n'),
        provenance: Provenance.OBSERVED,
      })
    },
    `No incident card available for ${input.nodeId}.`,
  )
}

export interface SemanticSearchInput {
  query: string
  project?: string
}

interface SearchResponse {
  query: string
  provider?: 'ollama' | 'transformers' | 'substring'
  matches: (GraphNode & { score?: number })[]
}

export async function semanticSearch(
  client: HttpClient,
  input: SemanticSearchInput,
): Promise<ToolResponse> {
  try {
    const result = await client.get<SearchResponse>(
      projectPath(input.project, `/search?q=${encodeURIComponent(input.query)}`),
    )
    if (result.matches.length === 0) {
      return formatEmptyResponse(`No matches for "${input.query}".`)
    }
    const provider = result.provider ?? 'substring'
    const blockLines: string[] = []
    let topScore: number | undefined
    for (const n of result.matches) {
      // Embedding tiers attach a cosine score in [0,1]; substring fallback
      // doesn't, so we elide the score when it's the placeholder 1.
      const score = provider !== 'substring' && typeof n.score === 'number' ? n.score : undefined
      const scoreBit = score !== undefined ? ` [score=${score.toFixed(2)}]` : ''
      if (score !== undefined && (topScore === undefined || score > topScore)) topScore = score
      blockLines.push(
        `  • ${n.id} (${n.type}) — ${(n as { name?: string }).name ?? n.id}${scoreBit}`,
      )
    }
    return formatToolResponse({
      summary: `Found ${result.matches.length} match${result.matches.length === 1 ? '' : 'es'} for "${input.query}" via ${provider} provider.`,
      block: blockLines.join('\n'),
      // Top similarity score doubles as a "how confident is the embedder
      // about the best match" signal. Substring provider returns no score —
      // the footer shows n/a in that case.
      confidence: topScore,
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface GraphDiffInput {
  againstSnapshot: string
  project?: string
}

interface GraphDiffResponse {
  base: { exportedAt?: string }
  current: { exportedAt: string }
  added: { nodes: GraphNode[]; edges: GraphEdge[] }
  removed: { nodes: GraphNode[]; edges: GraphEdge[] }
  changed: {
    nodes: { id: string; before: GraphNode; after: GraphNode }[]
    edges: { id: string; before: GraphEdge; after: GraphEdge }[]
  }
}

export async function getGraphDiff(
  client: HttpClient,
  input: GraphDiffInput,
): Promise<ToolResponse> {
  try {
    const result = await client.get<GraphDiffResponse>(
      projectPath(
        input.project,
        `/graph/diff?against=${encodeURIComponent(input.againstSnapshot)}`,
      ),
    )
    const total =
      result.added.nodes.length +
      result.added.edges.length +
      result.removed.nodes.length +
      result.removed.edges.length +
      result.changed.nodes.length +
      result.changed.edges.length
    const baseLabel = result.base.exportedAt ?? 'unknown'
    if (total === 0) {
      return formatEmptyResponse(
        `No differences between the current graph and ${input.againstSnapshot} (base exportedAt=${baseLabel}).`,
      )
    }
    const blockLines: string[] = [
      `  base exportedAt:    ${baseLabel}`,
      `  current exportedAt: ${result.current.exportedAt}`,
      '',
    ]
    if (result.added.nodes.length || result.added.edges.length) {
      blockLines.push('Added:')
      for (const n of result.added.nodes) blockLines.push(`  + node ${n.id} (${n.type})`)
      for (const e of result.added.edges)
        blockLines.push(`  + edge ${e.id} — ${e.source} -> ${e.target} (${e.type}, ${e.provenance})`)
      blockLines.push('')
    }
    if (result.removed.nodes.length || result.removed.edges.length) {
      blockLines.push('Removed:')
      for (const n of result.removed.nodes) blockLines.push(`  - node ${n.id} (${n.type})`)
      for (const e of result.removed.edges)
        blockLines.push(`  - edge ${e.id} — ${e.source} -> ${e.target} (${e.type}, ${e.provenance})`)
      blockLines.push('')
    }
    if (result.changed.nodes.length || result.changed.edges.length) {
      blockLines.push('Changed:')
      for (const c of result.changed.nodes) {
        blockLines.push(`  ~ node ${c.id} — ${summariseAttrDiff(c.before, c.after)}`)
      }
      for (const c of result.changed.edges) {
        const provBit =
          c.before.provenance !== c.after.provenance
            ? `provenance ${c.before.provenance} → ${c.after.provenance}`
            : summariseAttrDiff(c.before, c.after)
        blockLines.push(`  ~ edge ${c.id} — ${provBit}`)
      }
    }
    return formatToolResponse({
      summary: `Diff against ${input.againstSnapshot}: ${total} change${total === 1 ? '' : 's'} between the snapshot and the live graph.`,
      block: blockLines.join('\n').trimEnd(),
      // Diff results don't have a per-result provenance — the diff spans
      // every edge type and provenance kind. Footer shows n/a.
    })
  } catch (err) {
    if (err instanceof HttpError && err.status === 400) {
      return formatErrorResponse(
        `Could not load snapshot ${input.againstSnapshot}: ${err.message}`,
      )
    }
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

function summariseAttrDiff(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): string {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  const changed: string[] = []
  for (const k of keys) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) changed.push(k)
  }
  return changed.length === 0
    ? 'attributes differ'
    : `fields changed: ${changed.sort().join(', ')}`
}

export interface RecentStaleEdgesInput {
  limit?: number
  edgeType?: string
  project?: string
}

interface StaleEventResponse {
  edgeId: string
  source: string
  target: string
  edgeType: string
  thresholdMs: number
  ageMs: number
  lastObserved: string
  transitionedAt: string
}

export async function getRecentStaleEdges(
  client: HttpClient,
  input: RecentStaleEdgesInput,
): Promise<ToolResponse> {
  const params = new URLSearchParams()
  if (input.limit !== undefined) params.set('limit', String(input.limit))
  if (input.edgeType) params.set('edgeType', input.edgeType)
  const qs = params.size > 0 ? `?${params.toString()}` : ''

  try {
    const body = await client.get<{ count: number; total: number; events: StaleEventResponse[] }>(
      projectPath(input.project, `/stale-events${qs}`),
    )
    const events = body.events
    if (events.length === 0) {
      return formatEmptyResponse(
        input.edgeType
          ? `No stale ${input.edgeType} edges recorded.`
          : 'No stale-edge transitions recorded yet.',
      )
    }
    const blockLines = events.map(
      (e) =>
        `  ${e.transitionedAt} — ${e.source} -[${e.edgeType}]-> ${e.target}` +
        ` (last seen ${e.lastObserved}, threshold ${formatDuration(e.thresholdMs)})`,
    )
    return formatToolResponse({
      summary: `${events.length} stale-edge transition${events.length === 1 ? '' : 's'} recorded${input.edgeType ? ` for ${input.edgeType}` : ''}.`,
      block: blockLines.join('\n'),
      // STALE by definition — every event is a transition into STALE.
      provenance: Provenance.STALE,
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface CheckPoliciesInput {
  // 'all' (default) returns every current violation. 'unresolved' is reserved
  // for future resolution tracking — for the MVP it behaves the same as 'all'.
  // { policyId } narrows to violations of one named policy.
  scope?: 'all' | 'unresolved' | { policyId: string }
  // When provided, dry-run evaluation: return violations that *would* result
  // if the action were applied. Without it, return current violations.
  hypotheticalAction?: HypotheticalAction
  // Soft guardrail (ADR-108). When provided, return the policies APPLICABLE to
  // this node — the rules the agent should keep in mind while editing it,
  // surfaced as context. Informs only; never blocks. Takes precedence over the
  // violation-read and dry-run modes.
  applicableTo?: string
  project?: string
}

interface PoliciesCheckResponse {
  allowed: boolean
  hypotheticalAction?: HypotheticalAction
  violations: PolicyViolation[]
}

// check_policies — single MCP tool covering both state-read and dry-run modes
// per ADR-045. The contract explicitly rejects the audit's two-tool split
// (evaluate_policy + get_policy_violations); both modes route through here.
export async function checkPolicies(
  client: HttpClient,
  input: CheckPoliciesInput,
): Promise<ToolResponse> {
  try {
    // Soft guardrail mode (ADR-108). Surface the policies that govern the node
    // the agent is working at, as a labeled context block. It informs; it does
    // not block — there is no allowed/denied verdict on this path.
    if (input.applicableTo) {
      const body = await client.get<ApplicablePoliciesResponse>(
        projectPath(
          input.project,
          `/policies/applicable?node=${encodeURIComponent(input.applicableTo)}`,
        ),
      )
      return formatApplicablePolicies(body)
    }

    let violations: PolicyViolation[]
    let allowed = true
    let hypothetical: HypotheticalAction | undefined

    if (input.hypotheticalAction) {
      // Dry-run via POST /policies/check.
      const body = await postJson<PoliciesCheckResponse>(
        client,
        projectPath(input.project, '/policies/check'),
        { hypotheticalAction: input.hypotheticalAction },
      )
      violations = body.violations
      allowed = body.allowed
      hypothetical = body.hypotheticalAction
    } else {
      // State read via GET /policies/violations. Optional scope filters via
      // ?policyId=, severity isn't surfaced in the tool input today.
      const qsParams = new URLSearchParams()
      if (typeof input.scope === 'object' && 'policyId' in input.scope) {
        qsParams.set('policyId', input.scope.policyId)
      }
      const qs = qsParams.size > 0 ? `?${qsParams.toString()}` : ''
      const body = await client.get<{ violations: PolicyViolation[] }>(
        projectPath(input.project, `/policies/violations${qs}`),
      )
      violations = body.violations
      allowed = violations.every((v) => v.onViolation !== 'block')
    }

    if (violations.length === 0) {
      return formatEmptyResponse(
        hypothetical
          ? `No violations would result from the hypothetical action (${hypothetical.kind}).`
          : 'No policy violations recorded.',
      )
    }

    const blockCount = violations.filter((v) => v.onViolation === 'block').length
    const summaryParts: string[] = []
    if (hypothetical) {
      summaryParts.push(
        `Hypothetical ${hypothetical.kind} would surface ${violations.length} violation${violations.length === 1 ? '' : 's'}`,
      )
    } else {
      summaryParts.push(
        `${violations.length} policy violation${violations.length === 1 ? '' : 's'} currently recorded`,
      )
    }
    if (blockCount > 0) {
      summaryParts.push(`${blockCount} of which block`)
    }
    if (!allowed && hypothetical) {
      summaryParts.push('action denied')
    }
    const summary = summaryParts.join('; ') + '.'

    const blockLines = violations.map((v) => {
      const subject = v.subject.nodeId ?? v.subject.edgeId ?? v.subject.path?.[0] ?? '(global)'
      return `  • [${v.severity}/${v.onViolation}] ${v.policyName}: ${v.message} — ${subject}`
    })
    const severities = [...new Set(violations.map((v) => v.severity))]
    return formatToolResponse({
      summary,
      block: blockLines.join('\n'),
      // Confidence: hypothetical results inherit a 0.7 cap (the engine
      // can't fully simulate every action shape in MVP); confirmed
      // violations report 1.00 since the engine ran against current state.
      confidence: hypothetical ? 0.7 : 1,
      provenance: severities.join(' '),
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

// The soft-guardrail context block (ADR-108). This is the "hook to the top of
// agent memory": the policies that govern where the agent is working, surfaced
// so the rules ride along as it edits. It INFORMS — there is deliberately no
// allowed/denied verdict and no blocking language here. The post-launch kernel
// gate (ADR-093) is the only surface that refuses an action; this is not it.
function formatApplicablePolicies(body: ApplicablePoliciesResponse): ToolResponse {
  const { node, applicable } = body
  if (applicable.length === 0) {
    return formatEmptyResponse(
      `No policies apply to ${node}. Nothing to keep inside the lines here — and note this is advisory: NEAT surfaces policies for awareness, it never blocks your edit.`,
    )
  }
  const summary =
    `APPLICABLE POLICIES — ${applicable.length} ${applicable.length === 1 ? 'policy applies' : 'policies apply'} where you're working (${node}). ` +
    `Keep these in mind as you edit. They inform; they do not block. Nothing here gates or stops your change.`
  const lines = applicable.map((p) => {
    const tail = p.match === 'region' ? ' [nearby]' : ''
    return `  • [${p.severity}/${p.onViolation}] ${p.policyName}${tail}: ${p.reason}`
  })
  return formatToolResponse({
    summary,
    block: lines.join('\n'),
    // The policies themselves are declared in policy.json — EXTRACTED, fully
    // known; the confidence is in the rule's existence, not a guess.
    confidence: 1,
    provenance: 'EXTRACTED (policy.json)',
  })
}

// ──────────────────────────────────────────────────────────────────────────
// get_divergences (ADR-060) — the thesis surface
// ──────────────────────────────────────────────────────────────────────────

export interface DivergencesInput {
  type?: ReadonlyArray<DivergenceType>
  minConfidence?: number
  node?: string
  project?: string
}

function buildDivergencesPath(input: DivergencesInput): string {
  const params = new URLSearchParams()
  if (input.type && input.type.length > 0) params.set('type', input.type.join(','))
  if (input.minConfidence !== undefined) {
    params.set('minConfidence', String(input.minConfidence))
  }
  if (input.node) params.set('node', input.node)
  const qs = params.size > 0 ? `?${params.toString()}` : ''
  return projectPath(input.project, `/graph/divergences${qs}`)
}

function formatDivergenceLine(d: Divergence): string {
  switch (d.type) {
    case 'missing-observed':
    case 'missing-extracted':
      // Column locus (ADR-157 §4) — a column-grain drift on one `sql-table` node,
      // no edge. Edge locus — the declared/observed edge triple.
      if (d.column) {
        return `  • [${d.type}] ${d.table ?? d.source} column ${d.column} — confidence ${d.confidence.toFixed(2)}`
      }
      return `  • [${d.type}] ${d.source} → ${d.target} (${d.edgeType}) — confidence ${d.confidence.toFixed(2)}`
    case 'version-mismatch':
      return `  • [${d.type}] ${d.source} → ${d.target} — declared ${d.extractedVersion}, observed engine ${d.observedVersion} (${d.compatibility})`
    case 'host-mismatch':
      return `  • [${d.type}] ${d.source} → ${d.target} — declared host ${d.extractedHost}, observed host ${d.observedHost}`
    case 'compat-violation':
      return `  • [${d.type}] ${d.source} → ${d.target} — ${d.rule.kind}${d.rule.package ? ` (${d.rule.package})` : ''}`
    case 'observed-symbol-mismatch': {
      // Symbol/field-grain (ADR-215) — the code declares access to a member the
      // runtime object lacks. source == target (the code node), so name the
      // member and the declaring file:line instead of an edge.
      const at = d.location ? ` at ${d.location}` : ''
      const member = d.symbol ? ` ${d.symbol}` : ''
      return `  • [${d.type}] ${d.source}${member}${at} (${d.mismatchKind}) — confidence ${d.confidence.toFixed(2)}`
    }
    case 'observed-failing': {
      // Behavioral-failure (ADR-220). Edge locus — a declared+observed dependency
      // edge whose calls predominantly fail. Incident locus — source == target
      // (the declaring code node), named at the file:line.
      if (d.edgeType) {
        const rate =
          d.errorRate !== undefined ? ` ${Math.round(d.errorRate * 100)}% of observed calls fail` : ''
        return `  • [${d.type}] ${d.source} → ${d.target} (${d.edgeType}) —${rate} (${d.failureKind}) — confidence ${d.confidence.toFixed(2)}`
      }
      const at = d.location ? ` at ${d.location}` : ''
      return `  • [${d.type}] ${d.source}${at} (${d.failureKind}) — confidence ${d.confidence.toFixed(2)}`
    }
  }
}

export async function getDivergences(
  client: HttpClient,
  input: DivergencesInput,
): Promise<ToolResponse> {
  try {
    const result = await client.get<DivergenceResult>(buildDivergencesPath(input))
    if (result.totalAffected === 0) {
      return emptyWithReadiness(
        client,
        input.project,
        'No divergences found between the declared (EXTRACTED) and observed (OBSERVED) views of the graph.',
      )
    }
    // Sorted by confidence descending already; first entry is the headline.
    const headline = result.divergences[0]!
    const summary =
      `Found ${result.totalAffected} divergence${result.totalAffected === 1 ? '' : 's'} between code and production. ` +
      `Highest-confidence: ${headline.type} on ${headline.source} → ${headline.target}. ${headline.reason}`
    const blockLines: string[] = []
    for (const d of result.divergences) {
      blockLines.push(formatDivergenceLine(d))
      blockLines.push(`    reason: ${d.reason}`)
      blockLines.push(`    recommendation: ${d.recommendation}`)
    }
    const maxConfidence = result.divergences.reduce(
      (m, d) => Math.max(m, d.confidence),
      0,
    )
    return formatToolResponse({
      summary,
      block: blockLines.join('\n'),
      confidence: maxConfidence,
      // Composite provenance — divergences sit between EXTRACTED and
      // OBSERVED by construction; that's what makes them divergences.
      provenance: 'composite (EXTRACTED + OBSERVED)',
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

async function postJson<T>(
  client: HttpClient,
  path: string,
  body: unknown,
): Promise<T> {
  // The base HttpClient interface only exposes get(). For POST we need to
  // reach into the underlying transport. Most callers pass the client built
  // by createHttpClient which has post; types are kept minimal so test
  // stubs don't have to implement post unless the tool needs it.
  const c = client as HttpClient & { post?: <U>(p: string, b: unknown) => Promise<U> }
  if (typeof c.post !== 'function') {
    throw new Error('HttpClient does not support POST — required for check_policies dry-run')
  }
  return c.post<T>(path, body)
}

// ── /neat extend tools (ADR-081, ADR-086) ────────────────────────────────

export interface ListUninstrumentedInput {
  project?: string
}

interface LibraryCoverageResult {
  library: string
  coverage: string
  installedVersion?: string
  instrumentation_package?: string
  package_version?: string
  registration?: string
  notes?: string
}

export async function neatListUninstrumented(
  client: HttpClient,
  input: ListUninstrumentedInput,
): Promise<ToolResponse> {
  try {
    const result = await client.get<{ libraries: LibraryCoverageResult[] }>(
      projectPath(input.project, '/extend/list-uninstrumented'),
    )
    const libs = result.libraries
    if (libs.length === 0) {
      return formatEmptyResponse(
        'All detected libraries are covered by the auto-instrumentations bundle or the HTTP fallback. No extension needed.',
      )
    }
    const blockLines = libs.map((l) => {
      const pkgBit = l.instrumentation_package ? ` → ${l.instrumentation_package}@${l.package_version ?? '*'}` : ' → no registry entry'
      return `  • ${l.library} [${l.coverage}]${pkgBit}${l.notes ? ` — ${l.notes}` : ''}`
    })
    return formatToolResponse({
      summary: `${libs.length} librar${libs.length === 1 ? 'y needs' : 'ies need'} instrumentation beyond the auto-instrumentations bundle.`,
      block: blockLines.join('\n'),
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface LookupInstrumentationInput {
  library: string
  installedVersion?: string
  project?: string
}

export async function neatLookupInstrumentation(
  client: HttpClient,
  input: LookupInstrumentationInput,
): Promise<ToolResponse> {
  const qs = input.installedVersion ? `?library=${encodeURIComponent(input.library)}&version=${encodeURIComponent(input.installedVersion)}` : `?library=${encodeURIComponent(input.library)}`
  try {
    const result = await client.get<LibraryCoverageResult>(
      projectPath(input.project, `/extend/lookup${qs}`),
    )
    const lines = [
      `  coverage: ${result.coverage}`,
      ...(result.instrumentation_package ? [`  instrumentation_package: ${result.instrumentation_package}@${result.package_version ?? '*'}`] : []),
      ...(result.registration ? [`  registration: ${result.registration}`] : []),
      ...(result.notes ? [`  notes: ${result.notes}`] : []),
    ]
    return formatToolResponse({
      summary: `Registry entry for ${input.library}: coverage is ${result.coverage}.`,
      block: lines.join('\n'),
    })
  } catch (err) {
    if (err instanceof HttpError && err.status === 404) {
      return formatEmptyResponse(`${input.library} is not in the instrumentation registry.`)
    }
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface DescribeProjectInstrumentationInput {
  project?: string
}

interface ProjectInstrumentationState {
  hookFiles: string[]
  envNeat: boolean
  installedDeps: Record<string, string>
}

export async function neatDescribeProjectInstrumentation(
  client: HttpClient,
  input: DescribeProjectInstrumentationInput,
): Promise<ToolResponse> {
  try {
    const state = await client.get<ProjectInstrumentationState>(
      projectPath(input.project, '/extend/describe'),
    )
    const lines: string[] = [
      `  hook files:     ${state.hookFiles.length > 0 ? state.hookFiles.join(', ') : '(none — run neat init first)'}`,
      `  .env.neat:      ${state.envNeat ? 'present' : 'absent'}`,
    ]
    const depEntries = Object.entries(state.installedDeps)
    if (depEntries.length > 0) {
      lines.push('  installed OTel deps:')
      for (const [pkg, ver] of depEntries) {
        lines.push(`    ${pkg}@${ver}`)
      }
    } else {
      lines.push('  installed OTel deps: (none)')
    }
    const ready = state.hookFiles.length > 0
    return formatToolResponse({
      summary: ready
        ? `Project has ${state.hookFiles.length} instrumentation hook file${state.hookFiles.length === 1 ? '' : 's'} and is ready for neat_apply_extension.`
        : 'Project has no instrumentation hook files. Run neat init before extending.',
      block: lines.join('\n'),
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface ApplyExtensionInput {
  library: string
  instrumentation_package: string
  version: string
  registration_snippet: string
  project?: string
}

interface ExtensionApplyResult {
  library: string
  filesTouched: string[]
  depsAdded: string[]
  installOutput: string
  alreadyApplied: boolean
}

export async function neatApplyExtension(
  client: HttpClient,
  input: ApplyExtensionInput,
): Promise<ToolResponse> {
  try {
    const result = await postJson<ExtensionApplyResult>(
      client,
      projectPath(input.project, '/extend/apply'),
      {
        library: input.library,
        instrumentation_package: input.instrumentation_package,
        version: input.version,
        registration_snippet: input.registration_snippet,
      },
    )
    if (result.alreadyApplied) {
      return formatEmptyResponse(
        `${input.library} instrumentation is already applied — no changes made.`,
      )
    }
    const lines = [
      `  files touched: ${result.filesTouched.join(', ') || '(none)'}`,
      `  deps added:    ${result.depsAdded.join(', ') || '(none)'}`,
      `  install:       ${result.installOutput}`,
    ]
    return formatToolResponse({
      summary: `Applied ${input.instrumentation_package} for ${input.library}. ${result.filesTouched.length} file${result.filesTouched.length === 1 ? '' : 's'} touched, logged to ~/.neat/extend-log.ndjson.`,
      block: lines.join('\n'),
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface DryRunExtensionInput {
  library: string
  instrumentation_package: string
  version: string
  registration_snippet: string
  project?: string
}

interface ExtensionDiff {
  library: string
  filesTouched: string[]
  depsToAdd: string[]
  packageJsonPatch: object
  templatePatch: string
}

export async function neatDryRunExtension(
  client: HttpClient,
  input: DryRunExtensionInput,
): Promise<ToolResponse> {
  try {
    const result = await postJson<ExtensionDiff>(
      client,
      projectPath(input.project, '/extend/dry-run'),
      {
        library: input.library,
        instrumentation_package: input.instrumentation_package,
        version: input.version,
        registration_snippet: input.registration_snippet,
      },
    )
    const lines = [
      `  files that would be touched: ${result.filesTouched.join(', ') || '(none)'}`,
      `  deps to add:                 ${result.depsToAdd.join(', ') || '(none)'}`,
      `  hook file patch:             ${result.templatePatch}`,
    ]
    return formatToolResponse({
      summary: `Dry run for ${input.library}: ${result.filesTouched.length} file${result.filesTouched.length === 1 ? '' : 's'} would be touched. No changes made.`,
      block: lines.join('\n'),
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}

export interface RollbackExtensionInput {
  library: string
  project?: string
}

export async function neatRollbackExtension(
  client: HttpClient,
  input: RollbackExtensionInput,
): Promise<ToolResponse> {
  try {
    const result = await postJson<{ undone: boolean; message: string }>(
      client,
      projectPath(input.project, '/extend/rollback'),
      { library: input.library },
    )
    if (!result.undone) {
      return formatEmptyResponse(
        `No prior apply found for ${input.library} — nothing to roll back.`,
      )
    }
    return formatToolResponse({
      summary: `Rolled back instrumentation for ${input.library}. ${result.message}. Run your package manager install to sync the lockfile.`,
      block: `  result: ${result.message}`,
    })
  } catch (err) {
    return formatErrorResponse(`Error talking to neat-core: ${(err as Error).message}`)
  }
}
